import { type FocusEvent, useMemo, useRef, useState } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import type { FieldValues, Path } from 'react-hook-form'
import { useFormContext, useWatch } from 'react-hook-form'

import { isNonNullable } from '@subaio/utils'

import { useUnique } from '../../Unique'
import { useMergedRefs } from '../../useMergedRefs'
import type { CommonInputProps } from '../CommonProps'
import { useFormOptions } from '../useFormOptions'
import { validateFieldStruct } from '../utils'
import { fieldEmailOptional, fieldNumberOptional } from '../validators'

type InputMode = 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url'

type Props<TPath extends string> = Omit<
	CommonInputProps<TPath>,
	'label' | 'hint' | 'isFullWidth' | 'className' | 'style' | 'testId' | 'showValidationMessage' | 'renderSuffix'
>

export const useInput = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	showOptionalHint: fieldShowOptionalHint,
	isRequired = false,
	type = 'text',
	isClearable = false,
	isDisabled = false,
	maxLength,
	minLength,
	mode,
	name,
	autoComplete = 'off',
	isReadOnly = false,
	onBlur,
	onClick,
	placeholder,
	shouldAutofocus,
	validate,
}: Props<TPath>) => {
	const id = useUnique(name)
	const { register, resetField, formState, getFieldState } = useFormContext<TFieldValues>()
	const fieldValue = useWatch({ name, defaultValue: undefined })
	const { isDirty, invalid, error } = getFieldState(name, formState)
	const { isFocusVisible, focusProps, isFocused } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })
	const { hoverProps, isHovered } = useHover({})
	const [isForcedTextInput, setIsForcedTextInput] = useState(false)
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
	const { showChangedFields, showOptionalHint } = useFormOptions()

	const [initialValue] = useState(fieldValue)
	const inputMode: InputMode = useMemo(() => {
		if (mode) return mode
		switch (type) {
			case 'email':
				return 'email'
			case 'number':
				return 'numeric'
			case 'tel':
				return 'tel'
			default:
				return 'text'
		}
	}, [type, mode])
	const inputType = useMemo(() => {
		if (isForcedTextInput) return 'text'
		return type === 'number' ? 'text' : type
	}, [isForcedTextInput, type])

	const registeredInput = register(name, {
		required: isRequired ? (typeof isRequired === 'string' ? isRequired : 'This field is required') : false,
		disabled: isDisabled,
		validate: {
			email: (value) => (type === 'email' ? validateFieldStruct(value, fieldEmailOptional()) : true),
			number: (value) => (type === 'number' ? validateFieldStruct(value, fieldNumberOptional()) : true),
			...validate,
		},
		maxLength,
		minLength,
		setValueAs: (value: unknown) => {
			if (type === 'number') {
				if (typeof value === 'number') return value
				if (Number.isNaN(value) || !isNonNullable(value)) return initialValue === null ? null : undefined
				if (typeof value === 'string' && value.trim() !== '') return Number(value)
				return initialValue === null ? null : undefined
			}
			if (initialValue === null && !value) return null
			return typeof value === 'string' && value === '' ? undefined : value
		},
	})

	const ref = useMergedRefs<HTMLInputElement | HTMLTextAreaElement>(registeredInput.ref, inputRef)
	const hasError = Boolean(error)

	return {
		id,
		inputRef,
		fieldValue,
		hasError,
		isDirty,
		invalid,
		error,
		isFocusVisible,
		isFocused,
		isHovered,
		focusProps,
		hoverProps,
		showChangedFields,
		showOptionalHint,
		fieldShowOptionalHint,
		canClear: fieldValue !== undefined && isClearable && !isReadOnly && !isDisabled,
		isValueVisible: isForcedTextInput,
		clear: () => resetField(name),
		toggleValueVisibility: () => setIsForcedTextInput((value) => !value),
		inputProps: {
			...registeredInput,
			...(focusProps as Pick<typeof focusProps, 'onFocus' | 'onBlur'>),
			autoComplete,
			autoFocus: shouldAutofocus,
			id,
			inputMode,
			placeholder,
			readOnly: isReadOnly,
			disabled: isDisabled,
			type: inputType,
			onClick,
			onFocus: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				focusProps.onFocus?.(event)
			},
			onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
				onBlur?.(event)
				focusProps.onBlur?.(event)
				return registeredInput.onBlur(event)
			},
			ref,
		},
	}
}
